import { z } from "zod";
import { Cents, Id, IsoDate, IsoDateTime } from "../common.ts";
import { endpoint } from "../endpoint.ts";

/**
 * Invoicing (« Facturation », boards F0–F23, DOC-*, MX4–MX7) — the shared model.
 *
 * Every area contract (invoices.ts, quotes.ts, …) imports its entities from here,
 * so the screens, the A4 renderer and the mock all speak the same language.
 * Sources: FA-Etats (statuses, transitions, rights, automations), FA-Etats-2
 * (Iopole e-invoicing), FA-Parametres (settings, numbering, mentions), DOC-Principe
 * (document zones A–N), DOC-Modeles (6 templates), MX4–MX7 (plans and quotas).
 *
 * Conventions: money = integer cents (HT unless the name says Ttc), dates ISO,
 * codes in English snake_case, French copy in the *_META / *_LABELS tables.
 * Paths are session-scoped (the company comes from the session), like /accounting/….
 * Legacy API codes (BROUILLON, FACTUREE…) are only kept in `legacy` fields of the metas
 * for the API pass.
 */

/* ═════════════════════════ Display metadata ═════════════════════════ */

/** Same values as the Badge tones of components/ui/badge.tsx (kept local: contracts never import UI). */
export type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "muted" | "lime" | "dark" | "blue" | "purple";

/** Label + Badge tone for every status pill (StatusBadge reads these). */
export interface StatusMeta {
  label: string;
  tone: Tone;
  /** Code of the legacy API / FA-Etats, for the API pass. */
  legacy?: string;
  /** Short help shown in tooltips / filter hints. */
  hint?: string;
}

/* ═════════════════════════ Primitives ═════════════════════════ */

/** VAT rates offered in the line « TVA » picker (catalogue filter: 20 / 10 / 5,5 / 2,1 / 0). */
export const VAT_RATES = [20, 10, 5.5, 2.1, 0] as const;
export const VatRate = z.number().refine((v) => (VAT_RATES as readonly number[]).includes(v), { message: "Taux TVA invalide (0..100)" });
export type VatRate = (typeof VAT_RATES)[number];

export const Currency = z.literal("EUR");

/** « Pourcentage HT · 30 % » or « Remise (5 %) »: a percentage 0–100. */
export const Percent = z.number().min(0).max(100);

export const Address = z.object({
  line1: z.string(),
  /** « Complément d'adresse » (not printed on documents). */
  line2: z.string().nullable().default(null),
  postalCode: z.string(),
  city: z.string(),
  /** Country name as printed, "France". */
  country: z.string().default("France"),
});
export type Address = z.infer<typeof Address>;

export const FileRef = z.object({
  name: z.string(),
  sizeBytes: z.number().int(),
  /** Data URL in the mock, CDN URL in the API. */
  url: z.string().nullable().default(null),
});
export type FileRef = z.infer<typeof FileRef>;

/** Timeline entry of a document (« Créée en brouillon », « Finalisée — numéro FAC-… »). */
export const HistoryEvent = z.object({
  at: IsoDateTime,
  label: z.string(),
  detail: z.string().nullable().default(null),
});
export type HistoryEvent = z.infer<typeof HistoryEvent>;

/* ═════════════════════════ Numbering (FA-Parametres §3) ═════════════════════════ */

/**
 * PRÉFIXE-AAAA-NNNN on 4 digits, continuous, reset to 0001 each year (year of
 * finalisation), never changed once assigned. Drafts have no number (« #BROUILLON »).
 */
export const NUMBER_PREFIXES = { invoice: "FAC", quote: "DEV", credit_note: "AV", payment: "REG", refund: "REM" } as const;
export type NumberedKind = keyof typeof NUMBER_PREFIXES;
export const NumberedKind = z.enum(["invoice", "quote", "credit_note", "payment", "refund"]);

export function formatDocNumber(kind: NumberedKind, year: number, seq: number): string {
  return `${NUMBER_PREFIXES[kind]}-${year}-${String(seq).padStart(4, "0")}`;
}

/** Numeric order (0999 before 1000): compare year then sequence. */
export function compareDocNumbers(a: string | null, b: string | null): number {
  const parse = (n: string | null) => {
    const m = n?.match(/-(\d{4})-(\d+)$/);
    return m ? +m[1]! * 1e6 + +m[2]! : Number.POSITIVE_INFINITY; // drafts last
  };
  return parse(a) - parse(b);
}

/** « Dernier : FAC-2026-0014 » per kind (Paramètres › Numérotation). */
export const NumberingState = z.object({
  kind: NumberedKind,
  prefix: z.string(),
  year: z.number().int(),
  /** Last assigned sequence for `year` (0 = none yet). */
  last: z.number().int(),
  lastNumber: z.string().nullable(),
});
export type NumberingState = z.infer<typeof NumberingState>;

/* ═════════════════════════ Payment modes & terms ═════════════════════════ */

/** Règlement « Mode » (FA-Etats §8): VIR · CHQ · ESP · CRT · PRL. */
export const PaymentMode = z.enum(["transfer", "cheque", "cash", "card", "sepa"]);
export type PaymentMode = z.infer<typeof PaymentMode>;
export const PAYMENT_MODE_META: Record<PaymentMode, { code: string; label: string; legacy: string }> = {
  transfer: { code: "VIR", label: "Virement", legacy: "VIREMENT" },
  cheque: { code: "CHQ", label: "Chèque", legacy: "CHEQUE" },
  cash: { code: "ESP", label: "Espèces", legacy: "ESPECES" },
  card: { code: "CRT", label: "Carte bancaire", legacy: "CB" },
  sepa: { code: "PRL", label: "Prélèvement SEPA", legacy: "SEPA" },
};

/** « Conditions de paiement * » printed on the document: Virement (par défaut) · Chèque · Espèces · Carte bancaire. */
export const PaymentCondition = PaymentMode.exclude(["sepa"]);
export type PaymentCondition = z.infer<typeof PaymentCondition>;

/** « Délai de paiement * »: À réception · 15 jours · 30 jours (par défaut) · 60 jours · Choisir une date. */
export const PaymentTerm = z.enum(["on_receipt", "days_15", "days_30", "days_60", "custom"]);
export type PaymentTerm = z.infer<typeof PaymentTerm>;
export const PAYMENT_TERM_META: Record<PaymentTerm, { label: string; days: number | null }> = {
  on_receipt: { label: "À réception", days: 0 },
  days_15: { label: "15 jours", days: 15 },
  days_30: { label: "30 jours", days: 30 },
  days_60: { label: "60 jours", days: 60 },
  custom: { label: "Choisir une date", days: null },
};

/** « Durée de validité » of quotes (default 60 j) and credit notes (default 30 j). */
export const ValidityTerm = z.enum(["days_30", "days_60", "days_90", "custom"]);
export type ValidityTerm = z.infer<typeof ValidityTerm>;
export const VALIDITY_TERM_META: Record<ValidityTerm, { label: string; days: number | null }> = {
  days_30: { label: "30 jours", days: 30 },
  days_60: { label: "60 jours", days: 60 },
  days_90: { label: "90 jours", days: 90 },
  custom: { label: "Choisir une date", days: null },
};

/** One automatic reminder to the customer, chosen when sending: 7, 14 or 28 days after the issue date. */
export const ReminderDelay = z.union([z.literal(7), z.literal(14), z.literal(28)]).nullable();
export type ReminderDelay = z.infer<typeof ReminderDelay>;
export const REMINDER_OPTIONS: { value: ReminderDelay; label: string }[] = [
  { value: null, label: "Aucune relance" },
  { value: 7, label: "Relance après 7 jours" },
  { value: 14, label: "Relance après 14 jours" },
  { value: 28, label: "Relance après 28 jours" },
];

/** Add `days` to an ISO date (UTC-safe). */
export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Due date from the issue date and a term (custom → the given date). */
export function dueDateFor(issueDate: string, term: PaymentTerm, custom?: string | null): string | null {
  const days = PAYMENT_TERM_META[term].days;
  return days === null ? (custom ?? null) : addDays(issueDate, days);
}

/* ═════════════════════════ Customers (F14, F15) ═════════════════════════ */

export const CustomerKind = z.enum(["company", "individual"]);
export type CustomerKind = z.infer<typeof CustomerKind>;
export const CUSTOMER_KIND_META: Record<CustomerKind, { label: string; chip: string; hint: string }> = {
  company: { label: "Professionnel", chip: "Pro", hint: "Une entreprise : SIRET, raison sociale, TVA." },
  individual: { label: "Particulier", chip: "Particulier", hint: "Une personne : nom, prénom, contact." },
};

export const Contact = z.object({
  id: Id,
  lastName: z.string(),
  firstName: z.string().nullable(),
  /** « Fonction » (Gérante, Comptabilité…). */
  role: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  mobile: z.string().nullable(),
  note: z.string().nullable(),
  /** « Principal »: one per customer. */
  primary: z.boolean(),
});
export type Contact = z.infer<typeof Contact>;

export const CustomerNote = z.object({ id: Id, text: z.string().max(2000), at: IsoDateTime });
export type CustomerNote = z.infer<typeof CustomerNote>;

export const Customer = z.object({
  id: Id,
  kind: CustomerKind,
  /** Display name: raison sociale, or « Prénom Nom » for an individual. */
  name: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** Company only. */
  companyName: z.string().nullable(),
  siret: z.string().nullable(),
  vatNumber: z.string().nullable(),
  legalForm: z.string().nullable(),
  sector: z.string().nullable(),
  apeCode: z.string().nullable(),
  /** « Coordonnées du représentant » (company). */
  representative: z.object({ firstName: z.string(), lastName: z.string(), email: z.string().nullable(), phone: z.string().nullable() }).nullable(),
  address: Address.nullable(),
  email: z.string().nullable(),
  /** E.164, "+33320411822". */
  phone: z.string().nullable(),
  notes: z.array(CustomerNote),
  contacts: z.array(Contact),
  /** « Client depuis le 12/03/2026 ». */
  createdAt: IsoDateTime,
  /** Soft delete (history kept 10 years, restore offered on re-creation). */
  deletedAt: IsoDateTime.nullable(),
});
export type Customer = z.infer<typeof Customer>;

/** Money summary of a customer (list columns and fiche header). */
export const CustomerBalance = z.object({
  /** « Facturé TTC »: issued invoices (finalised, partially paid, paid). */
  invoicedTtcCents: Cents,
  /** « Encaissé »: payments − refunds. */
  collectedCents: Cents,
  /** « Avoirs »: finalised credit notes TTC. */
  creditNotesCents: Cents,
  /** « Solde dû » (negative = « en faveur du client »). */
  dueCents: Cents,
  /** « Crédit avoir »: free credit still available. */
  creditAvailableCents: Cents,
});
export type CustomerBalance = z.infer<typeof CustomerBalance>;

/** What a document freezes of its customer on save (« Nom et adresse figés sur le document à l'enregistrement »). */
export const CustomerSnapshot = z.object({
  kind: CustomerKind,
  name: z.string(),
  siret: z.string().nullable(),
  vatNumber: z.string().nullable(),
  address: Address.nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /** Outside France: no e-invoice, export VAT mention (R6). */
  foreign: z.boolean().default(false),
});
export type CustomerSnapshot = z.infer<typeof CustomerSnapshot>;

export function customerSnapshot(c: Customer): CustomerSnapshot {
  return {
    kind: c.kind,
    name: c.name,
    siret: c.siret,
    vatNumber: c.vatNumber,
    address: c.address,
    phone: c.phone,
    email: c.email,
    foreign: !!c.address && c.address.country !== "France",
  };
}

/** Option row of the editor's customer picker: « Boulangerie Lemoine · Professionnel · SIRET 824 117 305 00021 ». */
export const CustomerOption = z.object({
  id: Id,
  kind: CustomerKind,
  name: z.string(),
  siret: z.string().nullable(),
  email: z.string().nullable(),
  /** Free credit-note credit available (« Crédits avoir disponibles »). */
  creditAvailableCents: Cents,
});
export type CustomerOption = z.infer<typeof CustomerOption>;

/** Bodies of « Nouveau client » (editor window and F14), with the boards' messages. */
const phoneFr = z.string().regex(/^\+?[\d\s]+$/, "Le numéro doit contenir uniquement des chiffres");
export const NewIndividualBody = z.object({
  kind: z.literal("individual"),
  firstName: z.string().trim().min(1, "Le prénom est requis").min(2, "Au moins 2 caractères requis"),
  lastName: z.string().trim().min(1, "Le nom est requis"),
  email: z.string().trim().min(1, "L'email est requis").email("Format d'email invalide"),
  phone: phoneFr.min(1, "Le numéro de téléphone est requis"),
  address: Address.nullable().default(null),
});
export const NewCompanyBody = z.object({
  kind: z.literal("company"),
  siret: z.string().regex(/^\d{14}$/, "Le SIRET doit contenir 14 chiffres"),
  companyName: z.string().trim().min(1, "La raison sociale est requise"),
  legalForm: z.string().nullable().default(null),
  sector: z.string().trim().min(1, "Le secteur d'activité est requis"),
  apeCode: z.string().nullable().default(null),
  address: Address.extend({
    line1: z.string().trim().min(1, "L'adresse du siège est requise"),
    postalCode: z.string().regex(/^\d{5}$/, "Le code postal doit contenir 5 chiffres"),
    city: z.string().trim().min(1, "La ville est requise"),
  }),
  vatNumber: z
    .string()
    .regex(/^FR\d{11}$/, "Format de TVA invalide (ex: FR12345678901)")
    .nullable()
    .default(null),
  email: z.string().trim().min(1, "L'email est requis").email("Format d'email invalide"),
  phone: phoneFr.min(1, "Le numéro de téléphone est requis"),
  note: z.string().nullable().default(null),
});
export const NewCustomerBody = z.discriminatedUnion("kind", [NewIndividualBody, NewCompanyBody]);
export type NewCustomerBody = z.infer<typeof NewCustomerBody>;

/** INSEE lookup at 14 digits (« Informations SIRET récupérées avec succès »). */
export const SiretLookup = z.object({
  siret: z.string(),
  companyName: z.string(),
  legalForm: z.string().nullable(),
  sector: z.string().nullable(),
  apeCode: z.string().nullable(),
  vatNumber: z.string().nullable(),
  address: Address.nullable(),
  active: z.boolean(),
});
export type SiretLookup = z.infer<typeof SiretLookup>;

/* ═════════════════════════ Catalogue (F16) ═════════════════════════ */

/** Catalogue type; becomes the line type « Service » / « Bien » when copied into a document. */
export const CatalogueItemType = z.enum(["service", "product"]);
export type CatalogueItemType = z.infer<typeof CatalogueItemType>;
export const CATALOGUE_TYPE_LABELS: Record<CatalogueItemType, string> = { service: "Service", product: "Produit" };

export const CatalogueItem = z.object({
  id: Id,
  /** « Référence »: optional, unique per company. */
  reference: z.string().nullable(),
  /** « Nom de l'article * ». */
  name: z.string(),
  /** Stays in the catalogue, never copied into lines. */
  description: z.string().nullable(),
  type: CatalogueItemType,
  /** Free text, default « unité » (forfait, heure, m², pièce…). */
  unit: z.string(),
  currency: Currency,
  priceCents: Cents,
  vatRate: z.number(),
  /** « Proposé dans les devis et factures » (unticked → « Retiré »). */
  active: z.boolean(),
  createdAt: IsoDateTime,
});
export type CatalogueItem = z.infer<typeof CatalogueItem>;

/** Units suggested by the line « Unité » field (free text accepted). */
export const UNIT_SUGGESTIONS = ["unité", "forfait", "heure", "jour", "m²", "m", "pièce", "lettre", "an", "mois", "lot"] as const;

/* ═════════════════════════ Document lines & totals ═════════════════════════ */

/** Line type → « Catégorie de l'opération » (Prestation de services / Livraison de biens / Mixte). */
export const LineType = z.enum(["service", "goods"]);
export type LineType = z.infer<typeof LineType>;
export const LINE_TYPE_LABELS: Record<LineType, string> = { service: "Service", goods: "Bien" };

export const OperationCategory = z.enum(["services", "goods", "mixed"]);
export type OperationCategory = z.infer<typeof OperationCategory>;
export const OPERATION_CATEGORY_LABELS: Record<OperationCategory, string> = {
  services: "Prestation de services",
  goods: "Livraison de biens",
  mixed: "Mixte",
};

/** Derived from the line types (DOC-Principe zone C). */
export function operationCategoryOf(lines: readonly { type: LineType }[]): OperationCategory {
  const s = new Set(lines.map((l) => l.type));
  return s.size > 1 ? "mixed" : s.has("goods") ? "goods" : "services";
}

export const DocumentLine = z.object({
  id: Id,
  type: LineType,
  /** « Désignation »: sanitized rich HTML (b, i, ul/li, br), 300 words max. */
  designation: z.string(),
  quantity: z.number(),
  /** « Unité », default « unité ». */
  unit: z.string(),
  /** « Prix unitaire » HT; negative for balance deductions. */
  unitPriceCents: Cents,
  vatRate: z.number(),
  /** Line discount in % (not drawn on F3: the discount is global; kept for imports / API). */
  discountPercent: z.number().min(0).max(100).nullable().default(null),
  /** « Image » button of the line toolbar (to build). */
  imageUrl: z.string().nullable().default(null),
  /** Catalogue item it was copied from (values are copied, no live link). */
  catalogueItemId: Id.nullable().default(null),
  /**
   * Balance invoice: « Déduction facture d'acompte N° FAC-… du JJ/MM/AAAA », added by
   * the API on save (« Ajoutée à l'enregistrement »), locked in the editor.
   */
  deduction: z.object({ invoiceId: Id, invoiceNumber: z.string(), invoiceDate: IsoDate }).nullable().default(null),
});
export type DocumentLine = z.infer<typeof DocumentLine>;

/** « Remise globale » % or € « sur le total HT » (never per line on screen). */
export const GlobalDiscount = z.object({ kind: z.enum(["percent", "amount"]), value: z.number().min(0) });
export type GlobalDiscount = z.infer<typeof GlobalDiscount>;

/** Totals block as computed by lib/invoicing/totals.ts (the API returns the same). */
export const DocumentTotals = z.object({
  /** HT of each line, same order as `lines`. */
  lineCents: z.array(Cents),
  byRate: z.array(z.object({ rate: z.number(), grossCents: Cents, discountCents: Cents, baseCents: Cents, vatCents: Cents })),
  grossHtCents: Cents,
  discountCents: Cents,
  netHtCents: Cents,
  vatCents: Cents,
  ttcCents: Cents,
  creditsAppliedCents: Cents,
  dueCents: Cents,
});
export type DocumentTotals = z.infer<typeof DocumentTotals>;

/** IBAN / BIC printed in the payment block (prefilled from settings). */
export const BankDetails = z.object({ iban: z.string(), bic: z.string() });
export type BankDetails = z.infer<typeof BankDetails>;
export const IBAN_FR = /^FR\d{2}[A-Z0-9]{23}$/;
export const BIC_FR = /^[A-Z]{4}FR[A-Z0-9]{2}([A-Z0-9]{3})?$/;
export const IBAN_ERROR = "L'IBAN doit être au format français (27 caractères) : FR + 2 chiffres + 23 caractères";
export const BIC_ERROR = "Le BIC doit être au format français : 4 lettres + FR + 2 caractères (+ 3 caractères optionnels)";
export const compactIban = (s: string) => s.replace(/\s+/g, "").toUpperCase();
/** « FR76 1695 8000 0101 2345 6784 412 ». */
export const formatIban = (s: string) => compactIban(s).replace(/(.{4})/g, "$1 ").trim();

/* ═════════════════════════ Templates (DOC-Modeles, FA-Design-Editeur) ═════════════════════════ */

/** The 6 built-in designs; custom designs (« Créer mon design ») have their own id `dsg_…`. */
export const BUILTIN_TEMPLATES = ["onde", "prisme", "orbite", "classic", "modern", "minimal"] as const;
export const BuiltinTemplate = z.enum(BUILTIN_TEMPLATES);
export type BuiltinTemplate = z.infer<typeof BuiltinTemplate>;
/** A built-in id or a custom design id. */
export const DocumentTemplateId = z.string().min(1);
export type DocumentTemplateId = BuiltinTemplate | (string & {});

export const TEMPLATE_META: Record<BuiltinTemplate, { label: string; legacy: string; font: DocumentFont; description: string }> = {
  onde: { label: "Onde", legacy: "ONDE", font: "helvetica", description: "Vagues et pointillés en couleur principale, grand titre coloré, lignes zébrées, Total TTC en bandeau plein." },
  prisme: { label: "Prisme", legacy: "PRISME", font: "helvetica", description: "Bandeau sombre à coupe diagonale, triangles et hachures, tableau à en-tête foncé, Total TTC sur fond sombre." },
  orbite: { label: "Orbite", legacy: "ORBITE", font: "helvetica", description: "Cercles, anneaux et pastilles, numéro en pastille, tableau et totaux arrondis, Total TTC en pilule." },
  classic: { label: "Classique", legacy: "CLASSIC", font: "helvetica", description: "Logo ou nom en couleur principale, tableau à bandeau de couleur, totaux sur carte teintée, Total TTC en couleur secondaire." },
  modern: { label: "Moderne", legacy: "MODERN", font: "inter", description: "Bandeau pleine largeur en couleur principale, client en carte, informations en pastilles, Total TTC sur un bloc plein." },
  minimal: { label: "Minimal", legacy: "MINIMAL", font: "newsreader", description: "Noir et blanc, titre fin à empattements, filets fins, aucun aplat : idéal à imprimer." },
};
export const DEFAULT_TEMPLATE: BuiltinTemplate = "onde";

export const DocumentFont = z.enum(["helvetica", "inter", "newsreader", "lora", "courier"]);
export type DocumentFont = z.infer<typeof DocumentFont>;
export const FONT_LABELS: Record<DocumentFont, string> = { helvetica: "Helvetica", inter: "Inter", newsreader: "Newsreader", lora: "Lora", courier: "Courier" };

export const HexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Format attendu #RRGGBB");
export const BrandColors = z.object({
  /** « Couleur principale »: table header, totals block, accents. Default #5B4FE0. */
  primary: HexColor,
  /** « Couleur secondaire »: document title, Total TTC amount. Default #0F1613. */
  secondary: HexColor,
  /** « Couleur d'accent »: decorative shapes of Onde / Prisme / Orbite. Default #C4F82A. */
  accent: HexColor,
});
export type BrandColors = z.infer<typeof BrandColors>;
export const DEFAULT_COLORS: BrandColors = { primary: "#5B4FE0", secondary: "#0F1613", accent: "#C4F82A" };
export const COLOR_PRESETS = [
  { label: "Melrys", hex: "#5B4FE0" },
  { label: "Teal", hex: "#0F766E" },
  { label: "Bleu", hex: "#1D4ED8" },
  { label: "Vert", hex: "#16A34A" },
  { label: "Orange", hex: "#EA580C" },
  { label: "Rouge", hex: "#DC2626" },
  { label: "Noir", hex: "#0F1613" },
  { label: "Violet", hex: "#6D28D9" },
] as const;

/* ═════════════════════════ E-invoicing · Iopole (FA-Etats-2) ═════════════════════════ */

/**
 * Pastille « Facture électronique »: the 15 Iopole codes + « Non transmise ».
 * Independent from the Melrys status (an invoice can be « Payée » and « Rejetée »).
 * Individuals and foreign customers are never transmitted (cell « — »).
 */
export const EInvoiceStatus = z.enum([
  "not_transmitted",
  "submitted",
  "issued",
  "received",
  "made_available",
  "in_hand",
  "approved",
  "partially_approved",
  "disputed",
  "suspended",
  "completed",
  "refused",
  "payment_sent",
  "payment_received",
  "rejected",
  "unacceptable",
]);
export type EInvoiceStatus = z.infer<typeof EInvoiceStatus>;

/** Sales invoices (feminine labels). `code` = DGFiP status code when mandatory. */
export const EINVOICE_STATUS_META: Record<EInvoiceStatus, StatusMeta & { iopole: string | null; code?: number; mandatory?: boolean }> = {
  not_transmitted: { label: "Non transmise", tone: "muted", iopole: null },
  submitted: { label: "Déposée", tone: "info", iopole: "SUBMITTED", code: 200, mandatory: true },
  issued: { label: "Émise", tone: "info", iopole: "ISSUED" },
  received: { label: "Reçue par la PA du client", tone: "info", iopole: "RECEIVED" },
  made_available: { label: "Mise à disposition", tone: "info", iopole: "MADE_AVAILABLE" },
  in_hand: { label: "Prise en charge", tone: "info", iopole: "IN_HAND" },
  approved: { label: "Approuvée", tone: "success", iopole: "APPROVED" },
  partially_approved: { label: "Approuvée partiellement", tone: "success", iopole: "PARTIALLY_APPROVED" },
  disputed: { label: "En litige", tone: "warning", iopole: "DISPUTED" },
  suspended: { label: "Suspendue", tone: "warning", iopole: "SUSPENDED" },
  completed: { label: "Complétée", tone: "success", iopole: "COMPLETED" },
  refused: { label: "Refusée", tone: "danger", iopole: "REFUSED", code: 210, mandatory: true },
  payment_sent: { label: "Paiement annoncé", tone: "success", iopole: "PAYMENT_SENT" },
  payment_received: { label: "Encaissée", tone: "success", iopole: "PAYMENT_RECEIVED", code: 212, mandatory: true, hint: "déclarée au paiement" },
  rejected: { label: "Rejetée", tone: "danger", iopole: "REJECTED", code: 213, mandatory: true },
  unacceptable: { label: "Rejetée — non conforme", tone: "danger", iopole: "UNACCEPTABLE" },
};

/** Credit notes (masculine labels, no payment chips: the refund stays the Melrys status). */
export const CREDIT_NOTE_EINVOICE_META: Partial<Record<EInvoiceStatus, StatusMeta>> = {
  not_transmitted: { label: "Non transmis", tone: "muted" },
  submitted: { label: "Déposé", tone: "info" },
  issued: { label: "Émis", tone: "info" },
  received: { label: "Reçu par la PA du client", tone: "info" },
  made_available: { label: "Mis à disposition", tone: "info" },
  in_hand: { label: "Pris en charge", tone: "info" },
  approved: { label: "Accepté", tone: "success" },
  partially_approved: { label: "Accepté partiellement", tone: "success" },
  disputed: { label: "En litige", tone: "warning" },
  suspended: { label: "Suspendu", tone: "warning" },
  refused: { label: "Refusé", tone: "danger" },
  rejected: { label: "Rejeté", tone: "danger" },
  unacceptable: { label: "Rejeté — non conforme", tone: "danger" },
};

/** The quick filter « Facture électronique : toutes » (F2, F12). */
export const EINVOICE_FILTER_OPTIONS = ["not_transmitted", "submitted", "made_available", "approved", "disputed", "refused", "rejected", "payment_received"] as const;

/** Happy path of the chip (FA-Etats-2 §2 « Transitions »), exits listed separately. */
export const EINVOICE_FLOW: EInvoiceStatus[] = ["not_transmitted", "submitted", "issued", "received", "made_available", "in_hand", "approved", "payment_sent", "payment_received"];
export const EINVOICE_TRANSITIONS: Record<EInvoiceStatus, EInvoiceStatus[]> = {
  not_transmitted: ["submitted"],
  submitted: ["issued", "rejected", "unacceptable"],
  issued: ["received", "rejected", "unacceptable"],
  received: ["made_available"],
  made_available: ["in_hand", "approved", "partially_approved", "disputed", "suspended", "refused"],
  in_hand: ["approved", "partially_approved", "disputed", "suspended", "refused"],
  approved: ["payment_sent", "payment_received", "completed"],
  partially_approved: ["payment_sent", "payment_received", "disputed"],
  disputed: ["approved", "refused"],
  suspended: ["in_hand", "approved", "refused"],
  completed: [],
  refused: [],
  payment_sent: ["payment_received"],
  payment_received: [],
  rejected: [],
  unacceptable: [],
};

/** Iopole refusal / dispute reasons (28 codes; the common ones). */
export const EINVOICE_REASONS: Record<string, string> = {
  LEGAL_INFORMATION_MISSING: "Mentions légales manquantes",
  INCORRECT_RECIPIENT: "Destinataire incorrect",
  RECIPIENT_NOT_FOUND: "destinataire introuvable",
  VAT_RATE_INCORRECT: "Taux de TVA incorrect",
  TOTAL_AMOUNT_INCORRECT: "Montant total incorrect",
  DUPLICATE_INVOICE: "Facture en double",
  OTHER: "Autre motif",
};

export const EInvoiceState = z.object({
  status: EInvoiceStatus,
  at: IsoDateTime.nullable(),
  /** Iopole transmission id. */
  transmissionId: z.string().nullable(),
  /** Refusal / rejection reason shown under the chip (« destinataire introuvable »). */
  reason: z.string().nullable(),
  /** false for an individual or foreign customer: cell « — », nothing about Iopole. */
  applicable: z.boolean(),
});
export type EInvoiceState = z.infer<typeof EInvoiceState>;

/* ═════════════════════════ Invoices (FA-Etats §1) ═════════════════════════ */

/** Five statuses, one machine. « En retard » is computed (see isOverdue), never stored. */
export const InvoiceStatus = z.enum(["draft", "finalized", "partially_paid", "paid", "cancelled"]);
export type InvoiceStatus = z.infer<typeof InvoiceStatus>;
export const INVOICE_STATUS_META: Record<InvoiceStatus, StatusMeta> = {
  draft: { label: "Brouillon", tone: "neutral", legacy: "BROUILLON", hint: "Brouillon : rien n'est parti chez le client." },
  finalized: { label: "Finalisée", tone: "info", legacy: "FACTUREE" },
  partially_paid: { label: "Payée partiellement", tone: "warning", legacy: "PAYEE_PARTIELLEMENT" },
  paid: { label: "Payée", tone: "success", legacy: "PAYEE" },
  cancelled: { label: "Annulée", tone: "danger", legacy: "ANNULEE", hint: "Annulée par un avoir total." },
};
/** Computed badge « En retard · N j ». */
export const OVERDUE_META: StatusMeta = { label: "En retard", tone: "danger" };

/**
 * Allowed status changes. Manual: draft→finalized (« Finaliser »), draft→cancelled
 * (« Annuler le brouillon »), « Marquer payée » (finalized|partially_paid → paid|partially_paid).
 * Automatic: payments (→ partially_paid / paid, and back when a payment is removed),
 * total credit note (issued → cancelled). Issued → cancelled directly is FORBIDDEN
 * (only through a total credit note: use INVOICE_CANCEL_BY_CREDIT_NOTE).
 */
export const INVOICE_TRANSITIONS: Record<InvoiceStatus, InvoiceStatus[]> = {
  draft: ["finalized", "cancelled"],
  finalized: ["partially_paid", "paid", "cancelled"],
  partially_paid: ["paid", "finalized", "cancelled"],
  paid: ["partially_paid", "finalized", "cancelled"],
  cancelled: [],
};
/** Which → cancelled moves are only legal as the effect of a total credit note. */
export const INVOICE_CANCEL_BY_CREDIT_NOTE: InvoiceStatus[] = ["finalized", "partially_paid", "paid"];
export const INVOICE_DIRECT_CANCEL_REFUSAL = "Une facture finalisée ne peut pas être annulée directement. Veuillez créer un avoir total à la place.";

/** « Actions permises par statut » (FA-Etats §1). R15: locked for good once finalised. */
export type InvoiceAction = "view" | "edit" | "duplicate" | "send" | "delete" | "quick_payment" | "archive" | "credit_note" | "recurrence" | "mark_paid";
export const INVOICE_ACTIONS: Record<InvoiceStatus, InvoiceAction[]> = {
  draft: ["view", "edit", "duplicate", "send", "delete"],
  finalized: ["view", "duplicate", "quick_payment", "credit_note", "recurrence", "mark_paid"],
  partially_paid: ["view", "duplicate", "quick_payment", "credit_note", "recurrence", "mark_paid"],
  paid: ["view", "duplicate", "archive", "credit_note", "recurrence"],
  cancelled: ["view", "duplicate", "archive"],
};
export const canInvoice = (status: InvoiceStatus, action: InvoiceAction) => INVOICE_ACTIONS[status].includes(action);

/** Classique · Acompte · Solde (chosen in the creation assistant). */
export const InvoiceType = z.enum(["standard", "deposit", "balance"]);
export type InvoiceType = z.infer<typeof InvoiceType>;
export const INVOICE_TYPE_META: Record<InvoiceType, { label: string; title: string; badge: string | null; legacy: string; description: string }> = {
  standard: { label: "Classique", title: "Facture", badge: null, legacy: "CLASSIQUE", description: "Une facture standard couvrant l'intégralité du montant du devis." },
  deposit: { label: "Acompte", title: "Facture d'acompte", badge: "Acompte", legacy: "ACOMPTE", description: "Une facture pour percevoir un paiement partiel avant la livraison de la prestation." },
  balance: { label: "Solde", title: "Facture de solde", badge: "Solde", legacy: "SOLDE", description: "Émettre la facture finale pour le reste à payer après les acomptes." },
};

/** Deposit amount method (« Montant libre » · « Pourcentage HT » · « Pourcentage TTC »), also used by credit notes. */
export const AmountMethod = z.enum(["amount", "percent_ht", "percent_ttc"]);
export type AmountMethod = z.infer<typeof AmountMethod>;
export const AMOUNT_METHOD_META: Record<AmountMethod, { label: string; hint: string }> = {
  amount: { label: "Montant libre", hint: "Saisir manuellement le montant exact à facturer." },
  percent_ht: { label: "Pourcentage HT", hint: "Basé sur les montants HT ventilés par taux de TVA." },
  percent_ttc: { label: "Pourcentage TTC", hint: "Basé sur le total TTC du devis." },
};

/** Free credit-note credit imputed on an invoice (« Imputé à la création · rendu si le brouillon est supprimé »). */
export const AppliedCredit = z.object({ creditNoteId: Id, creditNoteNumber: z.string(), amountCents: Cents });
export type AppliedCredit = z.infer<typeof AppliedCredit>;

/* ═════════════════════════ Quotes (FA-Etats §2) ═════════════════════════ */

/** Six visible statuses (SUPPRIME / EXPIRE never shown; validity badges are computed). */
export const QuoteStatus = z.enum(["draft", "finalized", "sent", "signed", "invoiced", "cancelled"]);
export type QuoteStatus = z.infer<typeof QuoteStatus>;
export const QUOTE_STATUS_META: Record<QuoteStatus, StatusMeta> = {
  draft: { label: "Brouillon", tone: "neutral", legacy: "BROUILLON" },
  finalized: { label: "Finalisé, non envoyé", tone: "info", legacy: "NON_ENVOYE", hint: "Non envoyé" },
  sent: { label: "Envoyé", tone: "blue", legacy: "ENVOYE" },
  signed: { label: "Signé", tone: "success", legacy: "SIGNE" },
  invoiced: { label: "Facturé", tone: "purple", legacy: "FACTURE" },
  cancelled: { label: "Annulé", tone: "danger", legacy: "ANNULE" },
};
/**
 * Direct status matrix (PATCH /devis/:id {statut}); refusal « Transition de statut
 * invalide: ENVOYE → BROUILLON ». invoiced → signed = late signature / total credit
 * note on a deposit or balance; invoiced → cancelled only via a total credit note.
 */
export const QUOTE_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  draft: ["finalized", "sent", "signed", "cancelled"],
  finalized: ["sent", "signed", "cancelled"],
  sent: ["signed", "cancelled"],
  signed: ["invoiced", "cancelled"],
  invoiced: ["signed", "cancelled"],
  cancelled: [],
};
/** Computed validity tags (« Expire dans 7 j », « Validité dépassée »). */
export const QUOTE_WATCH_META = {
  expiring: { label: "Expire dans {n} j", tone: "warning" },
  expired: { label: "Validité dépassée", tone: "danger" },
} as const satisfies Record<string, StatusMeta>;
/** Auto-cancel 90 days after validity (03:00 job), only for finalized / sent quotes. */
export const QUOTE_AUTO_CANCEL_DAYS = 90;
export const QUOTE_MAX_UNLOCKS = 3;

export const QuoteSignatureMode = z.enum(["none", "electronic", "marked"]);
export type QuoteSignatureMode = z.infer<typeof QuoteSignatureMode>;
export const QUOTE_SIGNATURE_LABELS: Record<QuoteSignatureMode, string> = { none: "—", electronic: "Signé électroniquement", marked: "Marqué comme signé" };

/** BoldSign envelope timeline (FA-Devis-Signature). */
export const SignatureEnvelopeStatus = z.enum(["sent", "viewed", "signed", "reminded", "declined", "expired", "revoked"]);
export type SignatureEnvelopeStatus = z.infer<typeof SignatureEnvelopeStatus>;
export const SIGNATURE_ENVELOPE_META: Record<SignatureEnvelopeStatus, StatusMeta> = {
  sent: { label: "Envoyé", tone: "info" },
  viewed: { label: "Vu", tone: "info" },
  signed: { label: "Signé", tone: "success" },
  reminded: { label: "Relancé", tone: "warning" },
  declined: { label: "Refusé", tone: "danger" },
  expired: { label: "Expiré", tone: "muted" },
  revoked: { label: "Révoqué", tone: "muted" },
};

/* ═════════════════════════ Credit notes (FA-Etats §3) ═════════════════════════ */

export const CreditNoteStatus = z.enum(["draft", "finalized", "refunded", "cancelled", "on_hold"]);
export type CreditNoteStatus = z.infer<typeof CreditNoteStatus>;
export const CREDIT_NOTE_STATUS_META: Record<CreditNoteStatus, StatusMeta> = {
  draft: { label: "Brouillon", tone: "neutral", legacy: "BROUILLON", hint: "Sans effet sur la facture tant qu'il n'est pas finalisé" },
  finalized: { label: "Finalisé", tone: "info", legacy: "FINALISE" },
  refunded: { label: "Remboursé", tone: "success", legacy: "REMBOURSE" },
  cancelled: { label: "Annulé", tone: "danger", legacy: "ANNULE" },
  on_hold: { label: "En attente", tone: "warning", legacy: "EN_ATTENTE" },
};
export const CREDIT_NOTE_TRANSITIONS: Record<CreditNoteStatus, CreditNoteStatus[]> = {
  draft: ["finalized", "cancelled", "on_hold"],
  finalized: ["refunded", "cancelled", "on_hold"],
  refunded: [],
  cancelled: [],
  on_hold: [],
};
export type CreditNoteAction = "view" | "edit" | "duplicate" | "finalize" | "delete" | "send" | "refund" | "allocate" | "download";
export const CREDIT_NOTE_ACTIONS: Record<CreditNoteStatus, CreditNoteAction[]> = {
  draft: ["view", "edit", "duplicate", "finalize", "delete", "download"],
  finalized: ["view", "duplicate", "send", "refund", "allocate", "download"],
  refunded: ["view", "duplicate", "download"],
  cancelled: ["view", "duplicate", "download"],
  on_hold: ["view", "duplicate", "download"],
};

/** « Avoir sur une facture » / « Avoir libre (crédit client) ». */
export const CreditNoteKind = z.enum(["linked", "free"]);
export type CreditNoteKind = z.infer<typeof CreditNoteKind>;
export const CREDIT_NOTE_KIND_LABELS: Record<CreditNoteKind, string> = { linked: "Avoir sur une facture", free: "Avoir libre (crédit client)" };
export const CreditNoteScope = z.enum(["partial", "total"]);
export type CreditNoteScope = z.infer<typeof CreditNoteScope>;
export const CREDIT_NOTE_SCOPE_LABELS: Record<CreditNoteScope, string> = { partial: "Avoir partiel", total: "Avoir total" };
/** Free credit note: « Le rembourser directement » / « Le conserver en crédit » (not printed). */
export const FreeCreditUse = z.enum(["refund", "keep_credit"]);
export type FreeCreditUse = z.infer<typeof FreeCreditUse>;

/* ═════════════════════════ Payments & refunds (F6, F13) ═════════════════════════ */

/** SEPA « Suite du prélèvement »: Programmé → Prélevé ou Rejeté. */
export const SepaStatus = z.enum(["scheduled", "collected", "rejected"]);
export type SepaStatus = z.infer<typeof SepaStatus>;
export const SEPA_STATUS_META: Record<SepaStatus, StatusMeta> = {
  scheduled: { label: "Programmé", tone: "info" },
  collected: { label: "Prélevé", tone: "success" },
  rejected: { label: "Rejeté", tone: "danger" },
};
export const SEPA_REJECT_REASONS: Record<string, string> = {
  AC01: "IBAN incorrect",
  AC04: "Compte clos",
  AM04: "Solde insuffisant",
  MD01: "Pas de mandat",
  MS02: "Refus du client",
};

/** Refund direction: « Rendu » to a customer / « Reçu » from a supplier. */
export const RefundDirection = z.enum(["to_customer", "from_supplier"]);
export type RefundDirection = z.infer<typeof RefundDirection>;
export const REFUND_DIRECTION_META: Record<RefundDirection, { label: string; type: string; long: string }> = {
  to_customer: { label: "Rendu", type: "Client", long: "Rendu à un client" },
  from_supplier: { label: "Reçu", type: "Fournisseur", long: "Reçu d'un fournisseur" },
};
/** A recorded refund is never edited or deleted: one status. */
export const REFUND_STATUS_META: StatusMeta = { label: "Effectué", tone: "success" };

/* ═════════════════════════ Recurring invoices (F7) ═════════════════════════ */

export const RecurringStatus = z.enum(["active", "paused", "finished"]);
export type RecurringStatus = z.infer<typeof RecurringStatus>;
export const RECURRING_STATUS_META: Record<RecurringStatus, StatusMeta> = {
  active: { label: "Active", tone: "success" },
  paused: { label: "En pause", tone: "warning" },
  finished: { label: "Terminée", tone: "neutral" },
};
/** Active ⇄ En pause · Active → Terminée (cap reached). No deletion. */
export const RECURRING_TRANSITIONS: Record<RecurringStatus, RecurringStatus[]> = {
  active: ["paused", "finished"],
  paused: ["active"],
  finished: [],
};
export const RecurringFrequency = z.enum(["daily", "weekly", "monthly", "quarterly", "yearly"]);
export type RecurringFrequency = z.infer<typeof RecurringFrequency>;
export const FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
  daily: "Quotidienne",
  weekly: "Hebdomadaire",
  monthly: "Mensuelle",
  quarterly: "Trimestrielle",
  yearly: "Annuelle",
};
/** « Après N occurrences » (API) · « À une date » / « Sans fin » (design, to validate). */
export const RecurringEnd = z.enum(["after_count", "on_date", "never"]);
export type RecurringEnd = z.infer<typeof RecurringEnd>;

/** Next occurrence date: start + frequency × n. */
export function nthOccurrence(startDate: string, frequency: RecurringFrequency, n: number): string {
  const d = new Date(`${startDate.slice(0, 10)}T00:00:00Z`);
  if (frequency === "daily") d.setUTCDate(d.getUTCDate() + n);
  else if (frequency === "weekly") d.setUTCDate(d.getUTCDate() + 7 * n);
  else d.setUTCMonth(d.getUTCMonth() + n * (frequency === "monthly" ? 1 : frequency === "quarterly" ? 3 : 12));
  return d.toISOString().slice(0, 10);
}

/* ═════════════════════════ Purchases (FA-Etats §4, F17, F23) ═════════════════════════ */

export const PurchaseInvoiceStatus = z.enum(["received", "validated", "paid", "disputed", "cancelled"]);
export type PurchaseInvoiceStatus = z.infer<typeof PurchaseInvoiceStatus>;
export const PURCHASE_INVOICE_STATUS_META: Record<PurchaseInvoiceStatus, StatusMeta> = {
  received: { label: "Reçue", tone: "blue", legacy: "RECEIVED", hint: "Reçue à vérifier" },
  validated: { label: "Validée", tone: "info", legacy: "VALIDATED" },
  paid: { label: "Payée", tone: "success", legacy: "PAID" },
  disputed: { label: "Contestée", tone: "danger", legacy: "DISPUTED" },
  cancelled: { label: "Annulée", tone: "muted", legacy: "CANCELLED" },
};
/** Never Reçue → Payée (« Transition non autorisée : RECEIVED → PAID. »); contesting needs a reason. */
export const PURCHASE_INVOICE_TRANSITIONS: Record<PurchaseInvoiceStatus, PurchaseInvoiceStatus[]> = {
  received: ["disputed", "validated", "cancelled"],
  disputed: ["validated", "cancelled"],
  validated: ["paid", "cancelled"],
  paid: ["cancelled"],
  cancelled: [],
};

export const SupplierCreditNoteStatus = z.enum(["received", "accepted", "pending_refund", "refunded", "disputed", "cancelled"]);
export type SupplierCreditNoteStatus = z.infer<typeof SupplierCreditNoteStatus>;
export const SUPPLIER_CREDIT_NOTE_STATUS_META: Record<SupplierCreditNoteStatus, StatusMeta> = {
  received: { label: "Reçu", tone: "blue", legacy: "RECEIVED" },
  accepted: { label: "Accepté", tone: "info", legacy: "ACCEPTED" },
  pending_refund: { label: "Remboursement attendu", tone: "warning", legacy: "PENDING_REFUND" },
  refunded: { label: "Remboursé", tone: "success", legacy: "REFUNDED" },
  disputed: { label: "Contesté", tone: "danger", legacy: "DISPUTED" },
  cancelled: { label: "Annulé", tone: "muted", legacy: "CANCELLED" },
};
/** Contesting: « Le motif du litige doit faire au moins 10 caractères ». A linked supplier refund → refunded. */
export const SUPPLIER_CREDIT_NOTE_TRANSITIONS: Record<SupplierCreditNoteStatus, SupplierCreditNoteStatus[]> = {
  received: ["accepted", "disputed", "cancelled", "refunded"],
  accepted: ["pending_refund", "disputed", "cancelled", "refunded"],
  pending_refund: ["refunded", "cancelled"],
  disputed: ["accepted", "cancelled"],
  refunded: [],
  cancelled: [],
};

export const PurchaseSource = z.enum(["manual", "einvoice"]);
export type PurchaseSource = z.infer<typeof PurchaseSource>;
export const PURCHASE_SOURCE_LABELS: Record<PurchaseSource, string> = { manual: "Dépôt manuel", einvoice: "Facture électronique" };

/** Supplier « Facturation électronique » and « Origine » columns. */
export const SupplierChannel = z.enum(["platform", "email", "manual", "unknown"]);
export const SUPPLIER_CHANNEL_LABELS: Record<z.infer<typeof SupplierChannel>, string> = {
  platform: "Plateforme agréée",
  email: "E-mail",
  manual: "Manuelle",
  unknown: "Non renseignée",
};
export const SupplierOrigin = z.enum(["facturx", "manual", "directory"]);
export const SUPPLIER_ORIGIN_LABELS: Record<z.infer<typeof SupplierOrigin>, string> = { facturx: "Dépôt Factur-X", manual: "Saisie manuelle", directory: "Annuaire" };

/* ═════════════════════════ Bank (F20, F21, F22) ═════════════════════════ */

export const BankLinkKind = z.enum(["none", "invoices", "credit_note", "refund", "purchase"]);
export type BankLinkKind = z.infer<typeof BankLinkKind>;
/** Reconciliation « Mode »: Une facture · Plusieurs factures · Un avoir. */
export const ReconcileMode = z.enum(["single_invoice", "multiple_invoices", "credit_note"]);
export type ReconcileMode = z.infer<typeof ReconcileMode>;
export const RECONCILE_MODE_LABELS: Record<ReconcileMode, string> = { single_invoice: "Une facture", multiple_invoices: "Plusieurs factures", credit_note: "Un avoir" };
export const BANK_LINK_META: Record<"unlinked" | "linked" | "partial", StatusMeta> = {
  unlinked: { label: "Non lié", tone: "muted" },
  linked: { label: "Lié", tone: "success" },
  partial: { label: "Partiellement rapproché", tone: "warning" },
};
export const BankAccountType = z.enum(["current", "savings", "credit_card", "loan", "other"]);
export const BANK_ACCOUNT_TYPE_LABELS: Record<z.infer<typeof BankAccountType>, string> = {
  current: "Compte courant",
  savings: "Compte épargne",
  credit_card: "Carte de crédit",
  loan: "Prêt",
  other: "Autre",
};

/* ═════════════════════════ Payment links (F15, proposal) ═════════════════════════ */

export const PaymentLinkStatus = z.enum(["sent", "opened", "paid", "expired", "disabled"]);
export type PaymentLinkStatus = z.infer<typeof PaymentLinkStatus>;
export const PAYMENT_LINK_STATUS_META: Record<PaymentLinkStatus, StatusMeta> = {
  sent: { label: "Envoyé", tone: "info" },
  opened: { label: "Ouvert, pas payé", tone: "warning" },
  paid: { label: "Payé", tone: "success" },
  expired: { label: "Expiré", tone: "muted" },
  disabled: { label: "Désactivé", tone: "muted" },
};

/* ═════════════════════════ Plans, quotas, signature credits (MX4–MX7, F19) ═════════════════════════ */

/** Software plan of the invoicing product (sidebar chip « START · 2/5 »). */
export const InvoicingPlan = z.enum(["start", "pro", "business"]);
export type InvoicingPlan = z.infer<typeof InvoicingPlan>;
export const BillingCycle = z.enum(["monthly", "annual"]);
export type BillingCycle = z.infer<typeof BillingCycle>;

/** START is free for life (no trial, decision 01/10/2026). Annual = 13 months for the price of 12. */
export const PLAN_META: Record<InvoicingPlan, { label: string; monthlyHtCents: number; annualHtCents: number; quota: number | null; features: string[] }> = {
  start: { label: "START", monthlyHtCents: 0, annualHtCents: 0, quota: 5, features: ["5 factures, 5 devis, 5 clients", "Suivi des paiements"] },
  pro: {
    label: "PRO",
    monthlyHtCents: 699,
    annualHtCents: 8388,
    quota: null,
    features: ["Factures, devis et clients illimités", "Relances automatiques", "Signature électronique des devis", "Personnalisation et exports"],
  },
  business: {
    label: "BUSINESS",
    monthlyHtCents: 999,
    annualHtCents: 11988,
    quota: null,
    features: ["Tout PRO", "Multi-utilisateurs", "Support prioritaire 24/7", "Statistiques avancées"],
  },
};
/** Seat bought on top of a plan's included users (« 0,99 € HT / mois / utilisateur »), same price on every plan. */
export const EXTRA_SEAT_PRICE_CENTS = 99;
/** Plan features gating screens (« Fonctionnalités par forfait »). */
export const PlanFeature = z.enum(["electronic_signature", "auto_reminders", "recurring", "exports", "accounting_exports", "bank_transactions"]);
export type PlanFeature = z.infer<typeof PlanFeature>;
export const PLAN_FEATURES: Record<InvoicingPlan, PlanFeature[]> = {
  start: [],
  pro: ["electronic_signature", "auto_reminders", "recurring", "exports", "bank_transactions"],
  business: ["electronic_signature", "auto_reminders", "recurring", "exports", "accounting_exports", "bank_transactions"],
};

/** The 3 START counters (cancelled documents don't count). */
export const QuotaKind = z.enum(["invoices", "quotes", "customers"]);
export type QuotaKind = z.infer<typeof QuotaKind>;
export const QUOTA_META: Record<QuotaKind, { label: string; noun: string; limitMessage: string }> = {
  invoices: { label: "Factures", noun: "factures", limitMessage: "Limite atteinte: votre forfait gratuit autorise 5 factures. Mettez à niveau pour continuer." },
  quotes: { label: "Devis", noun: "devis", limitMessage: "Limite atteinte: votre forfait gratuit autorise 5 devis. Mettez à niveau pour continuer." },
  customers: { label: "Clients", noun: "clients", limitMessage: "Limite atteinte: votre forfait gratuit autorise 5 clients. Mettez à niveau pour continuer." },
};
/** API error code of a quota refusal (400). */
export const QUOTA_EXCEEDED = "quota_exceeded";

export const Usage = z.object({ used: z.number().int(), limit: z.number().int().nullable() });
export type Usage = z.infer<typeof Usage>;

export const Subscription = z.object({
  plan: InvoicingPlan,
  cycle: BillingCycle.nullable(),
  /** Paid plans: « Accès jusqu'au 03/10/2027 ». */
  paidUntil: IsoDate.nullable(),
  autoRenew: z.boolean(),
  /** Price guaranteed 24 months from subscription. */
  priceGuaranteedUntil: IsoDate.nullable(),
  /** Restricted account (ACCOUNT_LOCKED): suspension or subscription expired > 30 days. */
  restricted: z.enum(["suspended", "subscription_expired"]).nullable(),
  usage: z.record(QuotaKind, Usage),
  features: z.array(PlanFeature),
});
export type Subscription = z.infer<typeof Subscription>;

/** Sidebar chip: « START · 3/5 » (the most used START counter), « PRO », « BUSINESS ». */
export function planBadge(s: Pick<Subscription, "plan" | "usage">): string {
  if (s.plan !== "start") return PLAN_META[s.plan].label;
  const used = Math.max(...QuotaKind.options.map((k) => s.usage[k]?.used ?? 0));
  return `START · ${Math.min(used, 5)}/5`;
}

/** BoldSign signature credits (F19). Pack sizes and prices: « À FOURNIR » on the board, never invent. */
export const SignatureCredits = z.object({
  remaining: z.number().int(),
  consumed: z.number().int(),
  total: z.number().int(),
  /** « 12 crédits consommés sur 30 depuis le 10/07/2026 ». */
  since: IsoDate.nullable(),
  packs: z.array(z.object({ id: Id, credits: z.number().int().nullable(), priceHtCents: Cents.nullable() })),
});
export type SignatureCredits = z.infer<typeof SignatureCredits>;

/* ═════════════════════════ Settings (F18) ═════════════════════════ */

export const VatRegime = z.enum(["franchise", "reel_simplifie", "reel_normal"]);
export type VatRegime = z.infer<typeof VatRegime>;
export const VAT_REGIME_META: Record<VatRegime, { label: string; sub: string }> = {
  franchise: { label: "Franchise en base de TVA", sub: "Pas de TVA facturée" },
  reel_simplifie: { label: "Régime réel simplifié", sub: "TVA sur vos lignes" },
  reel_normal: { label: "Régime réel normal", sub: "TVA sur vos lignes" },
};

/** Printed VAT mentions (DOC-Principe zones F/G). */
export const VAT_MENTIONS = {
  onReceipts: "TVA sur les encaissements",
  onDebits: "Option pour le paiement de la taxe d'après les débits",
  franchise: "TVA non applicable, article L.223 et suivants du CIBS",
  franchiseBefore20260901: "TVA non applicable, art. 293 B du CGI",
  reverseCharge: "Autoliquidation par le preneur — art. 196 directive 2006/112/CE",
  export: "Exonération de TVA — art. 262 ter du CGI",
} as const;
export const LATE_PENALTIES_TEXT =
  "En cas de retard de paiement, une pénalité de 3 fois le taux d'intérêt légal sera appliquée, à laquelle s'ajoutera une indemnité forfaitaire pour frais de recouvrement de 40 €.";
export const NO_EARLY_DISCOUNT_TEXT = "Pas d'escompte pour paiement anticipé.";
export const QUOTE_ACCEPTANCE_TEXT = "Pour être accepté, le devis doit être daté, signé et suivi de la mention « Bon pour accord ».";
export const DEFAULT_FOOTER_MESSAGE = "Merci pour votre confiance !";

export const Socials = z.object({
  website: z.string().nullable(),
  linkedin: z.string().nullable(),
  instagram: z.string().nullable(),
  facebook: z.string().nullable(),
  x: z.string().nullable(),
  tiktok: z.string().nullable(),
});
export type Socials = z.infer<typeof Socials>;

/** « Identité légale » (9 fields locked once filled; only the support can correct them). */
export const SellerIdentity = z.object({
  /** « Nom de l'entreprise * » (header and « Facture de » block). */
  tradeName: z.string(),
  legalName: z.string(),
  siret: z.string().nullable(),
  vatNumber: z.string().nullable(),
  legalForm: z.string().nullable(),
  /** « Capital social » in cents (no capital for an EI). */
  capitalCents: Cents.nullable(),
  apeCode: z.string().nullable(),
  sector: z.string().nullable(),
  activityStartDate: IsoDate.nullable(),
  /** « Mentions légales » (rich text, grey block K). */
  legalMentions: z.string().nullable(),
  address: Address,
  phone: z.string().nullable(),
  /** emailContact, printed in the seller block. */
  email: z.string().nullable(),
  /** Uploaded logo (42 px high on the PDF). */
  logoUrl: z.string().nullable(),
  /** RCS city printed in the footer (« RCS LILLE »). */
  rcsCity: z.string().nullable(),
});
export type SellerIdentity = z.infer<typeof SellerIdentity>;
export const LOCKED_IDENTITY_FIELDS = ["legalName", "siret", "vatNumber", "legalForm", "capitalCents", "apeCode", "sector", "activityStartDate", "legalMentions"] as const;

export const NotificationEvent = z.enum(["invoice_paid", "invoice_overdue", "recurring_generated", "quote_signed"]);
export const NOTIFICATION_EVENT_META: Record<z.infer<typeof NotificationEvent>, { label: string; hint: string }> = {
  invoice_paid: { label: "Facture payée", hint: "Aussi : avoir intégralement remboursé, remboursement à effectuer." },
  invoice_overdue: { label: "Facture en retard", hint: "15 puis 30 jours après la date d'échéance." },
  recurring_generated: { label: "Nouvelle facture", hint: "Brouillon créé par une facture récurrente." },
  quote_signed: { label: "Devis signé", hint: "Aussi : devis qui expire dans 7 jours." },
};

/** Iopole enrolment (KYC → KYB → mandate). */
export const EInvoicingEnrollment = z.enum(["not_started", "identity", "company", "mandate", "registered"]);
export type EInvoicingEnrollment = z.infer<typeof EInvoicingEnrollment>;

export const CustomDesign = z.object({
  id: Id,
  name: z.string(),
  /** « Parti du modèle Classique ». */
  base: BuiltinTemplate,
  colors: BrandColors,
  font: DocumentFont,
  logoPosition: z.enum(["left", "center"]),
  logoHeightPx: z.number().int(),
  /** Shapes / texts of the design editor (owned by the design agent, opaque here). */
  layers: z.array(z.record(z.string(), z.unknown())),
  updatedAt: IsoDateTime,
});
export type CustomDesign = z.infer<typeof CustomDesign>;

export const InvoicingSettings = z.object({
  identity: SellerIdentity,
  vatRegime: VatRegime,
  /** « Option pour le paiement de la taxe d'après les débits ». */
  vatOnDebits: z.boolean(),
  socials: Socials,
  /** « Assurance professionnelle : {assureur} · {couverture} » (artisans, BTP). */
  insurance: z.object({ insurer: z.string(), coverage: z.string() }).nullable(),
  documents: z.object({
    paymentTerm: PaymentTerm,
    paymentCondition: PaymentCondition,
    quoteValidity: ValidityTerm,
    creditNoteValidity: ValidityTerm,
    printNoEarlyDiscount: z.boolean(),
    /** « Relance automatique par défaut » (14 j by default). */
    reminderDefault: ReminderDelay,
    /** Global switch « Relances de facture automatiques » (off by default). */
    autoReminders: z.boolean(),
  }),
  bank: z.object({
    iban: z.string().nullable(),
    bic: z.string().nullable(),
    /** « Préremplir sur les nouvelles factures et nouveaux devis ». */
    prefill: z.boolean(),
    /** « QR code de paiement sur les factures » (off by default; never on quotes / credit notes). */
    qrEnabled: z.boolean(),
  }),
  einvoicing: z.object({ enrollment: EInvoicingEnrollment, platformName: z.string() }),
  notifications: z.array(z.object({ event: NotificationEvent, email: z.boolean(), inApp: z.boolean() })),
  appearance: z.object({
    /** « Design par défaut » per document type. */
    defaults: z.object({ invoice: DocumentTemplateId, quote: DocumentTemplateId, credit_note: DocumentTemplateId }),
    colors: BrandColors,
    font: DocumentFont.nullable(),
    /** « Message par défaut en bas de document ». */
    footerMessage: z.string(),
    customDesigns: z.array(CustomDesign),
  }),
  numbering: z.array(NumberingState),
});
export type InvoicingSettings = z.infer<typeof InvoicingSettings>;

/* ═════════════════════════ Editor draft (F3 / F9 / F11 bodies) ═════════════════════════ */

/**
 * What the shared document editor produces. Invoice, quote and credit-note create /
 * update endpoints take this body (plus their own extra fields). Totals are never
 * sent: the API recomputes them with lib/invoicing/totals.ts.
 */
export const DocumentDraftBody = z.object({
  customerId: Id.nullable(),
  title: z.string().nullable(),
  orderNumber: z.string().nullable(),
  issueDate: IsoDate,
  serviceDate: IsoDate.nullable(),
  lines: z.array(DocumentLine),
  globalDiscount: GlobalDiscount.nullable(),
  bank: BankDetails.nullable(),
  freeText: z.string().nullable(),
  template: DocumentTemplateId,
  /** Invoices: due date + terms. */
  paymentTerm: PaymentTerm.nullable(),
  dueDate: IsoDate.nullable(),
  paymentCondition: PaymentCondition.nullable(),
  showQr: z.boolean(),
  creditsApplied: z.array(AppliedCredit),
  /** Quotes / credit notes: validity. */
  validityTerm: ValidityTerm.nullable(),
  validUntil: IsoDate.nullable(),
});
export type DocumentDraftBody = z.infer<typeof DocumentDraftBody>;

/* ═════════════════════════ Entities ═════════════════════════ */

/** Fields every sales document shares (invoice, quote, credit note). */
const SalesDocumentBase = z.object({
  id: Id,
  /** null while a draft. */
  number: z.string().nullable(),
  customerId: Id,
  /** Frozen on save. */
  customer: CustomerSnapshot,
  title: z.string().nullable(),
  issueDate: IsoDate,
  lines: z.array(DocumentLine),
  globalDiscount: GlobalDiscount.nullable(),
  totals: DocumentTotals,
  bank: BankDetails.nullable(),
  freeText: z.string().nullable(),
  template: DocumentTemplateId,
  history: z.array(HistoryEvent),
  /** Unlocks used (quotes / credit notes: max 3; invoices: never, R15). */
  unlockCount: z.number().int(),
  /** « Copie légale »: PDF archived at finalisation, kept 10 years 6 months. */
  legalCopy: z.object({ fileName: z.string(), frozenAt: IsoDateTime, keptUntil: IsoDate, sizeBytes: z.number().int() }).nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const Invoice = SalesDocumentBase.extend({
  status: InvoiceStatus,
  type: InvoiceType,
  orderNumber: z.string().nullable(),
  operationCategory: OperationCategory,
  serviceDate: IsoDate.nullable(),
  paymentTerm: PaymentTerm,
  dueDate: IsoDate,
  paymentCondition: PaymentCondition,
  currency: Currency,
  showQr: z.boolean(),
  /** Source quote (deposit / balance / converted). */
  quoteId: Id.nullable(),
  quoteNumber: z.string().nullable(),
  /** Deposit / balance: quote total and « Reste à facturer ». */
  orderTotalTtcCents: Cents.nullable(),
  remainingToInvoiceTtcCents: Cents.nullable(),
  creditsApplied: z.array(AppliedCredit),
  /** Computed: payments received (− SEPA not yet collected). */
  paidCents: Cents,
  /** Computed: finalised linked credit notes TTC. */
  creditNotesCents: Cents,
  /** « Reste à payer », never stored (TTC − règlements − avoirs − crédits). */
  remainingCents: Cents,
  finalizedAt: IsoDateTime.nullable(),
  sentAt: IsoDateTime.nullable(),
  sentTo: z.array(z.string()),
  /** One reminder, chosen at send (7 / 14 / 28 days after the issue date). */
  reminderDelayDays: ReminderDelay,
  reminderSentAt: IsoDateTime.nullable(),
  archivedAt: IsoDateTime.nullable(),
  /**
   * « Marquer payée » (F4, PATCH /statut): Payée without a recorded payment — the balance is no longer
   * followed, so recomputing the totals keeps « Payée » (only a total credit note can still cancel it).
   */
  markedPaidAt: IsoDateTime.nullable().optional(),
  /** Recurrence: « Modèle récurrent » or « Occurrence #n ». */
  recurrence: z.object({ scheduleId: Id, role: z.enum(["template", "occurrence"]), occurrence: z.number().int().nullable() }).nullable(),
  einvoice: EInvoiceState,
  paymentIds: z.array(Id),
  creditNoteIds: z.array(Id),
});
export type Invoice = z.infer<typeof Invoice>;

export const Quote = SalesDocumentBase.extend({
  status: QuoteStatus,
  validityTerm: ValidityTerm,
  validUntil: IsoDate,
  paymentCondition: PaymentCondition.nullable(),
  signatureMode: QuoteSignatureMode,
  /** « Marqué comme signé ». */
  markedSignedAt: IsoDateTime.nullable(),
  /** BoldSign: signer, date, reference; plus the envelope timeline. */
  signature: z
    .object({
      signerName: z.string(),
      signerEmail: z.string(),
      signedAt: IsoDateTime.nullable(),
      reference: z.string().nullable(),
      events: z.array(z.object({ status: SignatureEnvelopeStatus, at: IsoDateTime })),
    })
    .nullable(),
  sentAt: IsoDateTime.nullable(),
  finalizedAt: IsoDateTime.nullable(),
  invoiceIds: z.array(Id),
  /** « Déjà facturé » TTC (non-cancelled linked invoices). */
  invoicedTtcCents: Cents,
  /** « Facturé en totalité ». */
  fullyInvoiced: z.boolean(),
});
export type Quote = z.infer<typeof Quote>;

export const CreditNote = SalesDocumentBase.extend({
  status: CreditNoteStatus,
  kind: CreditNoteKind,
  scope: CreditNoteScope.nullable(),
  invoiceId: Id.nullable(),
  invoiceNumber: z.string().nullable(),
  invoiceDate: IsoDate.nullable(),
  /** « Motif * ». */
  reason: z.string(),
  amountMethod: AmountMethod.nullable(),
  percent: z.number().nullable(),
  validityTerm: ValidityTerm,
  validUntil: IsoDate.nullable(),
  /** Free credit note only. */
  freeUse: FreeCreditUse.nullable(),
  /** « Crédit dispo. » (free + finalised): TTC − imputed − allocated − refunded. */
  creditAvailableCents: Cents,
  refundedCents: Cents,
  finalizedAt: IsoDateTime.nullable(),
  sentAt: IsoDateTime.nullable(),
  einvoice: EInvoiceState,
  refundIds: z.array(Id),
  /** Where the free credit went (imputed / allocated). */
  allocations: z.array(z.object({ invoiceId: Id, invoiceNumber: z.string().nullable(), amountCents: Cents, at: IsoDateTime })),
});
export type CreditNote = z.infer<typeof CreditNote>;

export const Payment = z.object({
  id: Id,
  /** REG-AAAA-NNNN, assigned on save. */
  number: z.string(),
  customerId: Id,
  customerName: z.string(),
  /** null = « Règlement libre ». */
  invoiceId: Id.nullable(),
  invoiceNumber: z.string().nullable(),
  date: IsoDate,
  mode: PaymentMode,
  reference: z.string().nullable(),
  amountCents: Cents,
  /** « TVA incluse »: amount ÷ TTC × VAT of the invoice. */
  vatCents: Cents,
  /** « Partiel » chip. */
  partial: z.boolean(),
  attachments: z.array(FileRef),
  sepa: z
    .object({
      mandateRef: z.string(),
      mandateSignedAt: IsoDate,
      ibanMasked: z.string(),
      collectionDate: IsoDate,
      status: SepaStatus,
      rejectCode: z.string().nullable(),
      rejectedAt: IsoDate.nullable(),
    })
    .nullable(),
  /** « Issu de la banque »: created by a reconciliation. */
  bankTransactionId: Id.nullable(),
  receiptSentAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type Payment = z.infer<typeof Payment>;

export const Refund = z.object({
  id: Id,
  /** REM-AAAA-NNNN (one counter for customers and suppliers). */
  number: z.string(),
  direction: RefundDirection,
  partyId: Id,
  partyName: z.string(),
  creditNoteId: Id.nullable(),
  creditNoteNumber: z.string().nullable(),
  /** Supplier side. */
  supplierCreditNoteId: Id.nullable(),
  supplierCreditNoteNumber: z.string().nullable(),
  purchaseInvoiceId: Id.nullable(),
  invoiceId: Id.nullable(),
  mode: PaymentCondition,
  date: IsoDate,
  /** Positive number; the sign comes from `direction` (− rendu / + reçu). */
  amountCents: Cents,
  reference: z.string().nullable(),
  bankTransactionId: Id.nullable(),
  createdAt: IsoDateTime,
});
export type Refund = z.infer<typeof Refund>;

export const RecurringSchedule = z.object({
  id: Id,
  templateInvoiceId: Id,
  templateNumber: z.string().nullable(),
  title: z.string(),
  customerId: Id,
  customerName: z.string(),
  amountTtcCents: Cents,
  frequency: RecurringFrequency,
  startDate: IsoDate,
  end: RecurringEnd,
  /** Planned occurrences (≥ 1) when end = after_count. */
  count: z.number().int().min(1).nullable(),
  endDate: IsoDate.nullable(),
  /** « Jour d'envoi » 1–31 (stored, not used yet: occurrences stay drafts). */
  autoSendDay: z.number().int().min(1).max(31),
  remindersEnabled: z.boolean(),
  internalComment: z.string().nullable(),
  status: RecurringStatus,
  occurrencesCreated: z.number().int(),
  /** « — » when paused or finished. */
  nextDate: IsoDate.nullable(),
  occurrenceInvoiceIds: z.array(Id),
  createdAt: IsoDateTime,
});
export type RecurringSchedule = z.infer<typeof RecurringSchedule>;

export const Supplier = z.object({
  id: Id,
  name: z.string(),
  siret: z.string().nullable(),
  vatNumber: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: Address.nullable(),
  note: z.string().nullable(),
  channel: SupplierChannel,
  origin: SupplierOrigin,
  createdAt: IsoDateTime,
});
export type Supplier = z.infer<typeof Supplier>;

export const PurchaseInvoice = z.object({
  id: Id,
  supplierId: Id,
  supplierName: z.string(),
  /** « N° de sa facture » (unique per supplier). */
  number: z.string().nullable(),
  issueDate: IsoDate,
  receivedDate: IsoDate,
  dueDate: IsoDate.nullable(),
  htCents: Cents,
  vatCents: Cents,
  ttcCents: Cents,
  /** « Ce que vous avez acheté ». */
  description: z.string().nullable(),
  file: FileRef.nullable(),
  source: PurchaseSource,
  status: PurchaseInvoiceStatus,
  disputeReason: z.string().nullable(),
  statusAt: IsoDateTime.nullable(),
  paidAt: IsoDate.nullable(),
  bankTransactionId: Id.nullable(),
  /** Received through Iopole: buyer-side chip. */
  einvoice: EInvoiceStatus.nullable(),
});
export type PurchaseInvoice = z.infer<typeof PurchaseInvoice>;

export const SupplierCreditNote = z.object({
  id: Id,
  supplierId: Id,
  supplierName: z.string(),
  purchaseInvoiceId: Id.nullable(),
  purchaseInvoiceNumber: z.string().nullable(),
  /** Supplier's own number (never generated by Melrys). */
  number: z.string(),
  issueDate: IsoDate,
  htCents: Cents,
  vatCents: Cents,
  ttcCents: Cents,
  reason: z.string().nullable(),
  status: SupplierCreditNoteStatus,
  disputeReason: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type SupplierCreditNote = z.infer<typeof SupplierCreditNote>;

export const BankAccount = z.object({
  id: Id,
  name: z.string(),
  bankName: z.string(),
  type: BankAccountType,
  ibanMasked: z.string().nullable(),
  balanceCents: Cents,
  active: z.boolean(),
  /** Bridge-synchronised or « Manuel ». */
  manual: z.boolean(),
  lastSyncAt: IsoDateTime.nullable(),
});
export type BankAccount = z.infer<typeof BankAccount>;

export const BankTransaction = z.object({
  id: Id,
  accountId: Id,
  date: IsoDate,
  label: z.string(),
  /** Signed: + encaissement, − décaissement. */
  amountCents: Cents,
  link: z.object({
    kind: BankLinkKind,
    /** Documents linked with the allocated amount (invoices: one or several). */
    targets: z.array(z.object({ id: Id, label: z.string(), amountCents: Cents })),
    /** Payments (REG) or refund (REM) created by the link. */
    createdIds: z.array(Id),
  }),
  attachment: FileRef.nullable(),
  note: z.string().nullable(),
});
export type BankTransaction = z.infer<typeof BankTransaction>;

export const PaymentLink = z.object({
  id: Id,
  invoiceId: Id,
  invoiceNumber: z.string(),
  customerId: Id,
  amountCents: Cents,
  recipient: z.string(),
  message: z.string().nullable(),
  url: z.string(),
  status: PaymentLinkStatus,
  createdAt: IsoDateTime,
  openedAt: IsoDateTime.nullable(),
  expiresAt: IsoDate,
});
export type PaymentLink = z.infer<typeof PaymentLink>;

/* ═════════════════════════ Computed helpers ═════════════════════════ */

/** « En retard »: due date passed and status Finalisée or Payée partiellement. */
export function isOverdue(i: Pick<Invoice, "status" | "dueDate">, today: string): boolean {
  return (i.status === "finalized" || i.status === "partially_paid") && i.dueDate < today;
}

/** Days late (« En retard · 6 j »), 0 when not overdue. */
export function overdueDays(i: Pick<Invoice, "status" | "dueDate">, today: string): number {
  if (!isOverdue(i, today)) return 0;
  return Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${i.dueDate}T00:00:00Z`)) / 86_400_000);
}

/** Quote « À surveiller »: finalized / sent and validity within 0–7 days (expiring) or past (expired). */
export function quoteWatch(q: Pick<Quote, "status" | "validUntil">, today: string): { kind: "expiring" | "expired"; days: number } | null {
  if (q.status !== "finalized" && q.status !== "sent") return null;
  const days = Math.round((Date.parse(`${q.validUntil}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  if (days < 0) return { kind: "expired", days: -days };
  if (days <= 7) return { kind: "expiring", days };
  return null;
}

/* ═════════════════════════ Rendered document (DocumentPreview input) ═════════════════════════ */

export const RenderedDocKind = z.enum(["invoice", "deposit_invoice", "balance_invoice", "quote", "credit_note", "receipt", "refund_receipt"]);
export type RenderedDocKind = z.infer<typeof RenderedDocKind>;
export const RENDERED_DOC_LABELS: Record<RenderedDocKind, { title: string; seller: string; buyer: string; file: string }> = {
  invoice: { title: "Facture", seller: "Facture de", buyer: "Facturé à", file: "facture" },
  deposit_invoice: { title: "Facture d'acompte", seller: "Facture de", buyer: "Facturé à", file: "facture" },
  balance_invoice: { title: "Facture de solde", seller: "Facture de", buyer: "Facturé à", file: "facture" },
  quote: { title: "Devis", seller: "Devis de", buyer: "Devis pour", file: "devis" },
  credit_note: { title: "Avoir", seller: "Avoir de", buyer: "Avoir pour", file: "avoir" },
  receipt: { title: "Reçu de règlement", seller: "Reçu de", buyer: "Reçu pour", file: "reglement" },
  refund_receipt: { title: "Bon pour réception", seller: "Émis par", buyer: "Destinataire", file: "bon-reception" },
};

/**
 * Everything the A4 renderer needs, already resolved (no lookups): the editor, the
 * detail screens, the PDF preview and the design editor all build one of these
 * (lib/invoicing/document.ts → buildRenderedDocument) and hand it to <DocumentPreview>.
 */
export const RenderedDocument = z.object({
  kind: RenderedDocKind,
  /** null = draft: « #BROUILLON » (credit note draft: « # »). */
  number: z.string().nullable(),
  seller: z.object({
    name: z.string(),
    logoUrl: z.string().nullable(),
    address: Address.nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
  }),
  buyer: CustomerSnapshot.nullable(),
  /** « Livré à » when the delivery address differs. */
  deliveryAddress: Address.nullable(),
  /** Zone C, left column then right column, already labelled, only filled rows. */
  meta: z.object({
    orderNumber: z.string().nullable(),
    title: z.string().nullable(),
    operationCategory: OperationCategory.nullable(),
    issueDate: IsoDate,
    dueDate: IsoDate.nullable(),
    serviceDate: IsoDate.nullable(),
    validUntil: IsoDate.nullable(),
    /** Deposit / balance: « Montant total de la commande », « Reste à facturer ». */
    orderTotalTtcCents: Cents.nullable(),
    remainingToInvoiceTtcCents: Cents.nullable(),
    /** Credit note: « Facture liée : FAC-… du JJ/MM/AAAA ». */
    linkedInvoice: z.object({ number: z.string(), date: IsoDate }).nullable(),
  }),
  lines: z.array(DocumentLine),
  totals: DocumentTotals,
  globalDiscount: GlobalDiscount.nullable(),
  /** « Net à payer » row (balance invoice / credits imputed). */
  showNetToPay: z.boolean(),
  payment: z.object({
    bank: BankDetails.nullable(),
    /** QR SEPA (invoices only, when ticked). */
    qr: z.boolean(),
    condition: PaymentCondition.nullable(),
  }),
  mentions: z.object({
    /** Zone F: « TVA sur les encaissements » or the debits option. */
    vat: z.string().nullable(),
    /** Zone G: franchise / reverse charge / export. */
    regime: z.string().nullable(),
    penalties: z.string().nullable(),
    noEarlyDiscount: z.boolean(),
    legal: z.string().nullable(),
    insurance: z.string().nullable(),
  }),
  /** Zone L: rich text, default « Merci pour votre confiance ! ». */
  freeText: z.string().nullable(),
  /** Zone M (quotes): « Bon pour accord » frames, or the electronic signature block once signed. */
  signature: z
    .object({
      signed: z.boolean(),
      signerName: z.string().nullable(),
      signerEmail: z.string().nullable(),
      signedAt: IsoDateTime.nullable(),
      reference: z.string().nullable(),
    })
    .nullable(),
  footer: z.object({
    socials: Socials,
    /** « Atelier Roux SASU au capital de 1 000 € · Siège : … · SIREN … – RCS LILLE · TVA … · APE … ». */
    legalLine: z.string(),
  }),
  design: z.object({ colors: BrandColors, font: DocumentFont.nullable() }),
  /**
   * Receipt template only (kind receipt / refund_receipt, DOC-Recu-Reglement, DOC-Bon-Reception):
   * the card « Date de paiement · Mode de paiement · Référence · Facture associée · Montant reçu ».
   * Built by lib/invoicing/document.ts → buildReceiptDocument. Added by the documents pass.
   */
  receipt: z
    .object({
      date: IsoDate,
      mode: PaymentMode,
      reference: z.string().nullable(),
      /** « FAC-2026-0042 — 5 472,00 € », « Règlement libre », « AVF-2026-0077 ». */
      linked: z.string().nullable(),
      amountCents: Cents,
      /** Partial payment: « Reste à payer » row (proposal D6), else null. */
      remainingCents: Cents.nullable(),
    })
    .nullable()
    .optional(),
});
export type RenderedDocument = z.infer<typeof RenderedDocument>;

/* ═════════════════════════ Endpoints shared by several areas ═════════════════════════ */

/** Price of one plan × cycle, as the server bills it today (HT, VAT 20 %, TTC; annual = 13 months for 12). */
export const PlanPrice = z.object({
  htCents: Cents,
  vatCents: Cents,
  ttcCents: Cents,
  /** Months of access paid (1 monthly, 13 annual). */
  months: z.number().int(),
  /** End of the access if subscribed today (« Accès jusqu'au 23/10/2026 »). */
  accessUntil: IsoDate,
});
export type PlanPrice = z.infer<typeof PlanPrice>;

/** One software plan as offered by the server (« Passer à l'abonnement », quota windows, « Mon abonnement »). */
export const PlanOffer = z.object({
  plan: InvoicingPlan,
  label: z.string(),
  /** START: 5 per counter; null = unlimited. */
  quota: z.number().int().nullable(),
  features: z.array(z.string()),
  monthly: PlanPrice,
  annual: PlanPrice,
});
export type PlanOffer = z.infer<typeof PlanOffer>;

/** Everything a screen needs to know about the invoicing space (plan, usage, editor defaults). */
export const InvoicingContext = z.object({
  companyName: z.string(),
  subscription: Subscription,
  /** Sidebar chip, e.g. « START · 3/5 ». */
  planBadge: z.string(),
  signatureCredits: SignatureCredits,
  /** Settings the editors need (identity, defaults, bank, appearance). */
  settings: InvoicingSettings,
  /** Server « today » (mock clock), for overdue / validity badges and the default periods of the filters / exports. */
  today: IsoDate,
  /** Accounting active on the same company (MX6/MX7: no « Vue transactions » in invoicing). */
  accountingActive: z.boolean(),
  /** The three plans with today's prices (START, PRO, BUSINESS, in that order). The screens never hold a price. */
  plans: z.array(PlanOffer),
});
export type InvoicingContext = z.infer<typeof InvoicingContext>;

export const invoicingApi = {
  /** GET the invoicing space context (plan, quotas, settings for the editors). */
  context: endpoint({ id: "invoicing.context", method: "GET", path: "/invoicing/context", response: InvoicingContext }),
  /** Editor customer picker: « Rechercher un client par nom ou SIRET... ». */
  customerOptions: endpoint({
    id: "invoicing.customers.options",
    method: "GET",
    path: "/invoicing/customers/options",
    query: z.object({ q: z.string().optional() }),
    response: z.object({ items: z.array(CustomerOption) }),
  }),
  /** One customer (editor « Facturé à » block, fiche header). */
  customer: endpoint({ id: "invoicing.customers.get", method: "GET", path: "/invoicing/customers/:customerId", params: z.object({ customerId: Id }), response: Customer }),
  /** « Nouveau client » window (editor + F14). 400 quota_exceeded, 409 duplicates. */
  createCustomer: endpoint({ id: "invoicing.customers.create", method: "POST", path: "/invoicing/customers", body: NewCustomerBody, response: Customer }),
  /** INSEE lookup at 14 digits. 404 « SIRET non trouvé ou établissement inactif ». */
  siretLookup: endpoint({ id: "invoicing.siret.lookup", method: "GET", path: "/invoicing/siret/:siret", params: z.object({ siret: z.string() }), response: SiretLookup }),
  /** « Choisir un élément du catalogue »: active items only. */
  catalogueOptions: endpoint({
    id: "invoicing.catalogue.options",
    method: "GET",
    path: "/invoicing/catalogue/options",
    query: z.object({ q: z.string().optional() }),
    response: z.object({ items: z.array(CatalogueItem) }),
  }),
  /** Free credit notes of a customer still holding credit (« Crédits avoir disponibles »). */
  customerCredits: endpoint({
    id: "invoicing.customers.credits",
    method: "GET",
    path: "/invoicing/customers/:customerId/credits",
    params: z.object({ customerId: Id }),
    response: z.object({ items: z.array(z.object({ creditNoteId: Id, number: z.string(), issueDate: IsoDate, ttcCents: Cents, availableCents: Cents })) }),
  }),
};
